import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, LabelList, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { getCapexSchedule } from '../../api/financials_capex';
import './CapexCharts.css';

/* ACQUISITIONS, VALEUR NETTE ET AMORTISSEMENTS (prototype d'Olivier,
   2026-09-30) : deux graphiques sous la liste, à la place de la table de
   synthèse. À gauche, les acquisitions OU la valeur nette (bascule) ; à droite,
   la charge d'amortissement. Une fenêtre commune : la synthèse annuelle, ou
   une année lue mois par mois. Rien ne se calcule ici : l'annuel vient de la
   liste servie par le serveur, le mensuel de son échéancier. */

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function CapexCharts({ tid, rows, years, unit }) {
  const p = useP();
  const k = unit === 'chf' ? 1 : 1000;
  const [win, setWin] = usePersistentValue(`capex_charts_win_${tid}`, '');
  const [left, setLeft] = usePersistentValue(`capex_charts_left_${tid}`, 'acq');
  const [sched, setSched] = useState(null);
  const annual = !win || !years.includes(Number(win));

  useEffect(() => {
    if (!tid || annual) { setSched(null); return undefined; }
    let alive = true;
    getCapexSchedule(tid, Number(win))
      .then((r) => { if (alive) setSched(r && !r.error ? r.data?.new || null : null); })
      .catch(() => { if (alive) setSched(null); });
    return () => { alive = false; };
  }, [tid, win, annual, rows]);

  const data = useMemo(() => {
    if (annual) {
      return years.map((y) => ({
        x: String(y),
        acq: rows.filter((r) => r.acq_year === y).reduce((s, r) => s + num(r.amount), 0) / k,
        net: rows.reduce((s, r) => s + num(r.years?.[y]?.net), 0) / k,
        amort: rows.reduce((s, r) => s + num(r.years?.[y]?.amort), 0) / k,
      }));
    }
    if (!sched) return [];
    return MOIS.map((lbl, i) => ({
      x: lbl,
      acq: num(sched.acquisitions?.[i + 1] ?? sched.acquisitions?.[String(i + 1)]) / k,
      net: num(sched.net?.[i + 1] ?? sched.net?.[String(i + 1)]) / k,
      amort: num(sched.amort?.[i + 1] ?? sched.amort?.[String(i + 1)]) / k,
    }));
  }, [annual, years, rows, sched, k]);

  const span = annual ? (years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : String(years[0] || '')) : String(win);
  const fmt = (v) => num(v).toLocaleString('de-CH', { maximumFractionDigits: unit === 'chf' ? 0 : 2 });
  const sum = (key) => data.reduce((s, d) => s + num(d[key]), 0);
  const last = (key) => (data.length ? num(data[data.length - 1][key]) : 0);
  const uLbl = unit === 'chf' ? p('Francs') : p('Milliers de francs');

  const leftKpi = left === 'acq' ? sum('acq') : last('net');
  const leftKpiTag = left === 'acq' ? span : `${p('fin')} ${annual ? years[years.length - 1] : win}`;
  const leftSub = left === 'acq'
    ? (annual ? p('Acquisitions de chaque année') : p('Acquisitions de chaque mois'))
    : (annual ? p('Valeur nette en fin d\'année') : p('Valeur nette en fin de mois'));

  const axes = (
    <>
      <CartesianGrid stroke="var(--color-border-inner)" vertical={false} />
      <XAxis dataKey="x" tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }}
             axisLine={{ stroke: 'var(--color-border)' }} tickLine={false} />
      <YAxis tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }} axisLine={false} tickLine={false}
             width={48} tickFormatter={fmt} />
      <Tooltip formatter={(v) => fmt(v)} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
    </>
  );
  const bars = (key) => (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 22, right: 8, bottom: 4, left: 0 }}>
        {axes}
        <Bar dataKey={key} fill="var(--color-accent)" radius={[2, 2, 0, 0]} maxBarSize={annual ? 72 : 28}>
          {annual && <LabelList dataKey={key} position="top" formatter={fmt}
                                 style={{ fontSize: 12, fill: 'var(--color-text)' }} />}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
  const line = (key) => (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 22, right: 16, bottom: 4, left: 0 }}>
        {axes}
        <Line type="linear" dataKey={key} stroke="var(--color-accent)" strokeWidth={2}
              dot={{ r: 3, fill: 'var(--color-accent)' }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <section className="cxc">
      <div className="cxc-head">
        <h2 className="cxc-title">{p('Acquisitions, valeur nette et amortissements')}</h2>
        <SegmentedToggle size="lg" value={annual ? '' : String(win)} onChange={setWin}
          options={[{ value: '', label: p('Synthèse annuelle') },
            ...years.map((y) => ({ value: String(y), label: String(y) }))]} />
      </div>
      <div className="cxc-duo">
        <div className="cxc-card">
          <div className="cxc-card__h">
            <SegmentedToggle value={left} onChange={setLeft}
              options={[{ value: 'acq', label: p('Acquisitions') }, { value: 'net', label: p('Valeur nette') }]} />
            <span className="cxc-kpi"><b>{fmt(leftKpi)}</b> · {leftKpiTag}</span>
          </div>
          <div className="cxc-sub">{leftSub}</div>
          <div className="cxc-unit">{uLbl}</div>
          {left === 'net' && !annual ? line('net') : bars(left)}
          <div className="cxc-foot">{p('Montants hors taxe')}</div>
        </div>
        <div className="cxc-card">
          <div className="cxc-card__h">
            <h3 className="cxc-card__t">{p('Amortissements')}</h3>
            <span className="cxc-kpi"><b>{fmt(sum('amort'))}</b> · {span}</span>
          </div>
          <div className="cxc-sub">{annual ? p('Charge de chaque année') : p('Charge de chaque mois')}</div>
          <div className="cxc-unit">{uLbl}</div>
          {bars('amort')}
          <div className="cxc-foot">{p('Nouvelles acquisitions seulement, hors actifs existants')}</div>
        </div>
      </div>
    </section>
  );
}
